import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { getStorageUrl } from '../../services/apiService'
import MemberParticipation from '../../components/client-admin/MemberParticipation'

const PlayerDetail = () => {
  const { playerId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [player, setPlayer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [states, setStates] = useState([])
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [participation, setParticipation] = useState(null)
  const [loadingParticipation, setLoadingParticipation] = useState(false)

  const [formData, setFormData] = useState({
    full_name: '',
    father_name: '',
    date_of_birth: '',
    gender: 'M',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    pin_code: '',
    district: '',
    state_id: '',
    position: '',
    category: '',
    aadhar_card: '',
    passport_number: '',
    contact: '',
    mobile: '',
    emergency_contact: '',
    email: '',
    marital_status: ''
  })

  const positions = [
    'Player', 'Pitcher', 'Catcher', 'First Baseman', 'Second Baseman',
    'Third Baseman', 'Shortstop', 'Left Fielder', 'Center Fielder', 'Right Fielder'
  ]

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

  useEffect(() => {
    fetchPlayerDetails()
    fetchStates()
  }, [playerId])

  const fetchPlayerDetails = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/user/my-players/${playerId}${source ? '?source=' + source : ''}`)
      if (response.data.success) {
        const playerData = response.data.data
        setPlayer(playerData)

        // Load tournament participation (dates/year + best medal for players)
        setParticipation(null)
        setLoadingParticipation(true)
        api.get(`/user/my-players/${playerId}/participation${source ? '?source=' + source : ''}`)
          .then(res => { if (res.data.success) setParticipation(res.data.data) })
          .catch(err => console.error('Error loading participation:', err))
          .finally(() => setLoadingParticipation(false))

        setFormData({
          full_name: playerData.full_name || '',
          father_name: playerData.father_name || '',
          date_of_birth: playerData.date_of_birth || '',
          gender: playerData.gender?.toUpperCase() || 'M',
          blood_group: playerData.blood_group || '',
          height: playerData.height || '',
          weight: playerData.weight || '',
          address: playerData.address || '',
          pin_code: playerData.pin_code || '',
          district: playerData.district || '',
          state_id: playerData.state_id || '',
          position: playerData.position || '',
          category: playerData.category || '',
          aadhar_card: playerData.aadhar_card || '',
          passport_number: playerData.passport_number || '',
          contact: playerData.contact || '',
          mobile: playerData.mobile || '',
          emergency_contact: playerData.emergency_contact || '',
          email: playerData.email || '',
          marital_status: playerData.marital_status || ''
        })

        if (playerData.player_image) {
          setImagePreview(getStorageUrl(playerData.player_image))
        }
      }
    } catch (error) {
      console.error('Error fetching player details:', error)
      alert('Failed to fetch player details')
      navigate('/user/my-players')
    } finally {
      setLoading(false)
    }
  }

  const fetchStates = async () => {
    try {
      const response = await api.get('/user/states')
      if (response.data.success) {
        setStates(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching states:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Image size should be less than 2MB')
        return
      }
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()

    if (!formData.full_name || !formData.father_name || !formData.date_of_birth || !formData.state_id) {
      alert('Please fill all required fields')
      return
    }

    setSaving(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('_method', 'PUT')

      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key])
        }
      })

      if (imageFile) {
        formDataToSend.append('player_image', imageFile)
      }

      const response = await api.post(`/user/my-players/${playerId}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        alert('Player updated successfully!')
        setIsEditing(false)
        fetchPlayerDetails()
      }
    } catch (error) {
      console.error('Error updating player:', error)
      alert(error.response?.data?.message || 'Failed to update player')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setImageFile(null)
    fetchPlayerDetails()
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this player?')) return

    try {
      await api.delete(`/user/my-players/${playerId}`)
      alert('Player deleted successfully')
      navigate('/user/my-players')
    } catch (error) {
      console.error('Error deleting player:', error)
      alert(error.response?.data?.message || 'Failed to delete player')
    }
  }

  if (loading) {
    return (
      <div className="player-detail-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading player details...</p>
        </div>
        <style>{getStyles()}</style>
      </div>
    )
  }

  if (!player) {
    return (
      <div className="player-detail-page">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle"></i>
          <p>Player not found</p>
          <button onClick={() => navigate('/user/my-players')} className="btn-back-main">
            Back to My Players
          </button>
        </div>
        <style>{getStyles()}</style>
      </div>
    )
  }

  const getGenderDisplay = (gender) => {
    if (!gender) return 'N/A'
    return gender.toUpperCase() === 'M' ? 'Male' : 'Female'
  }

  const getRegisterDate = (d) => {
    if (!d) return 'N/A'
    const date = new Date(String(d).replace(' ', 'T'))
    if (isNaN(date.getTime())) return d
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  return (
    <div className="player-detail-page">
      {/* Page Header with gradient background */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-id-card"></i>
          </div>
          <div className="header-text">
            <h1>Player ID - {player.player_reg_id}</h1>
            <p>Complete player information and details</p>
          </div>
        </div>
        {player.status === 'active' && (
          <div style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 18px', borderRadius: '8px',
            background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
            fontSize: '0.9rem', fontWeight: 700, whiteSpace: 'nowrap'
          }}>
            <i className="fas fa-check-circle"></i> Player Approved
          </div>
        )}
        <button className="btn-back" style={{ marginLeft: player.status === 'active' ? '0.75rem' : 'auto' }} onClick={() => navigate('/user/my-players')}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Player Detail Card */}
      <div className="player-card">
        {isEditing ? (
          /* Edit Mode */
          <form onSubmit={handleSave} className="edit-form">
            {/* Image Section */}
            <div className="detail-row image-row">
              <div className="detail-label">Image</div>
              <div className="detail-value image-cell">
                <div className="player-image-container">
                  {imagePreview ? (
                    <img src={imagePreview} alt={player.full_name} />
                  ) : (
                    <div className="no-image">
                      <i className="fas fa-user"></i>
                    </div>
                  )}
                </div>
                <label className="btn-upload">
                  <i className="fas fa-camera"></i> Change Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>

            {/* Form Fields */}
            <div className="detail-row">
              <div className="detail-label">Name <span className="required">*</span></div>
              <div className="detail-value">
                <input type="text" name="full_name" value={formData.full_name} onChange={handleInputChange} required />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Father Name <span className="required">*</span></div>
              <div className="detail-value">
                <input type="text" name="father_name" value={formData.father_name} onChange={handleInputChange} required />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">D.O.B <span className="required">*</span></div>
              <div className="detail-value">
                <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange} required />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Gender <span className="required">*</span></div>
              <div className="detail-value">
                <select name="gender" value={formData.gender} onChange={handleInputChange} required>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Blood Group</div>
              <div className="detail-value">
                <select name="blood_group" value={formData.blood_group} onChange={handleInputChange}>
                  <option value="">Select Blood Group</option>
                  {bloodGroups.map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Height</div>
              <div className="detail-value">
                <input type="number" name="height" value={formData.height} onChange={handleInputChange} placeholder="cm" step="0.1" />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Weight</div>
              <div className="detail-value">
                <input type="number" name="weight" value={formData.weight} onChange={handleInputChange} placeholder="kg" step="0.1" />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Address</div>
              <div className="detail-value">
                <textarea name="address" value={formData.address} onChange={handleInputChange} rows="3" />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">District</div>
              <div className="detail-value">
                <input type="text" name="district" value={formData.district} onChange={handleInputChange} />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">State <span className="required">*</span></div>
              <div className="detail-value">
                <select name="state_id" value={formData.state_id} onChange={handleInputChange} required>
                  <option value="">Select State</option>
                  {states.map(state => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Post code</div>
              <div className="detail-value">
                <input type="text" name="pin_code" value={formData.pin_code} onChange={handleInputChange} maxLength="6" />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Position</div>
              <div className="detail-value">
                <select name="position" value={formData.position} onChange={handleInputChange}>
                  <option value="">Select Position</option>
                  {positions.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Aadhar</div>
              <div className="detail-value">
                <input type="text" name="aadhar_card" value={formData.aadhar_card} onChange={handleInputChange} maxLength="12" />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Passport</div>
              <div className="detail-value">
                <input type="text" name="passport_number" value={formData.passport_number} onChange={handleInputChange} />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Contact</div>
              <div className="detail-value">
                <input type="tel" name="contact" value={formData.contact} onChange={handleInputChange} maxLength="10" />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Emergency Contact</div>
              <div className="detail-value">
                <input type="tel" name="emergency_contact" value={formData.emergency_contact} onChange={handleInputChange} maxLength="10" />
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Email</div>
              <div className="detail-value">
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
              </div>
            </div>

            <div className="detail-row" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg, #fff1f1 0%, #ffe4e6 100%)', border: '1px solid #f6b8bd', borderRadius: '14px', padding: '14px 18px', marginTop: '10px', boxShadow: '0 3px 12px rgba(192,15,21,0.12)' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'linear-gradient(135deg, #e11d2a 0%, #c00f15 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 8px rgba(192,15,21,0.35)' }}>
                <i className="fas fa-calendar-check" style={{ color: '#fff', fontSize: '1.25rem' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.7rem', letterSpacing: '0.09em', textTransform: 'uppercase', color: '#c00f15', fontWeight: 700 }}>Registered On</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#7a0a0e', lineHeight: 1.25 }}>{getRegisterDate(player?.created_at)}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button type="submit" className="btn-action btn-save" disabled={saving}>
                <i className="fas fa-save"></i> {saving ? 'SAVING...' : 'SAVE CHANGES'}
              </button>
              <button type="button" className="btn-action btn-cancel" onClick={handleCancel} disabled={saving}>
                <i className="fas fa-times"></i> CANCEL
              </button>
            </div>
          </form>
        ) : (
          /* View Mode */
          <>
            {/* Image Row */}
            <div className="detail-row image-row">
              <div className="detail-label">Image</div>
              <div className="detail-value image-cell">
                <div className="player-image-container">
                  {imagePreview ? (
                    <img src={imagePreview} alt={player.full_name} />
                  ) : (
                    <div className="no-image">
                      <i className="fas fa-user"></i>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Detail Rows */}
            <div className="detail-row">
              <div className="detail-label">Name</div>
              <div className="detail-value">{player.full_name || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Father Name</div>
              <div className="detail-value">{player.father_name || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">D.O.B.</div>
              <div className="detail-value">{player.date_of_birth || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Gender</div>
              <div className="detail-value">{getGenderDisplay(player.gender)}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Blood Group</div>
              <div className="detail-value">{player.blood_group || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Height</div>
              <div className="detail-value">{player.height ? `${player.height}` : 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Weight</div>
              <div className="detail-value">{player.weight ? `${player.weight}` : 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Address</div>
              <div className="detail-value">{player.address || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">District</div>
              <div className="detail-value">{player.district || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">State</div>
              <div className="detail-value">{player.state_name || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Post code</div>
              <div className="detail-value">{player.pin_code || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Position</div>
              <div className="detail-value">{player.position || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Aadhar</div>
              <div className="detail-value">{player.aadhar_card || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Passport</div>
              <div className="detail-value">{player.passport_number || 'NA'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Contact</div>
              <div className="detail-value">{player.contact || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Emergency Contact</div>
              <div className="detail-value">{player.emergency_contact || 'N/A'}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Email</div>
              <div className="detail-value">{player.email || 'N/A'}</div>
            </div>

            <div className="detail-row" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg, #fff1f1 0%, #ffe4e6 100%)', border: '1px solid #f6b8bd', borderRadius: '14px', padding: '14px 18px', marginTop: '10px', boxShadow: '0 3px 12px rgba(192,15,21,0.12)' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'linear-gradient(135deg, #e11d2a 0%, #c00f15 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 8px rgba(192,15,21,0.35)' }}>
                <i className="fas fa-calendar-check" style={{ color: '#fff', fontSize: '1.25rem' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.7rem', letterSpacing: '0.09em', textTransform: 'uppercase', color: '#c00f15', fontWeight: 700 }}>Registered On</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#7a0a0e', lineHeight: 1.25 }}>{getRegisterDate(player.created_at)}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              {player.status === 'pending' && (
                <>
                  <button className="btn-action btn-edit" onClick={() => setIsEditing(true)}>
                    <i className="fas fa-edit"></i> EDIT DETAILS
                  </button>
                  <button className="btn-action btn-delete" onClick={handleDelete}>
                    <i className="fas fa-trash"></i> DELETE PLAYER
                  </button>
                </>
              )}
              {(player.status === 'inactive' || player.status === 'Inactive') && (
                <div className="blocked-badge" style={{
                  padding: '12px 20px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: '1px solid #fecaca'
                }}>
                  <i className="fas fa-ban"></i> Player Blocked by Admin
                </div>
              )}
            </div>

            {/* Tournament participation — players only (not coaches, managers or officials) */}
            {participation?.is_player && (
              <div style={{ padding: '0 1.5rem 1.5rem' }}>
                <MemberParticipation data={participation} loading={loadingParticipation} />
              </div>
            )}
          </>
        )}
      </div>

      <style>{getStyles()}</style>
    </div>
  )
}

const getStyles = () => `
  .player-detail-page {
    padding: 0;
    width: 100%;
    min-height: 100vh;
    background: #f3f4f6;
  }

  .page-header-banner {
    background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
    padding: 1.5rem 2rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
    border-radius: 12px;
    margin: 1.5rem;
    margin-bottom: 0;
  }

  .btn-back {
    height: 40px;
    padding: 0 18px;
    border-radius: 8px;
    border: none;
    background: #c00f15;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 0.9rem;
    white-space: nowrap;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .btn-back:hover {
    background: #a00d12;
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon {
    width: 50px;
    height: 50px;
    background: rgba(255,255,255,0.15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .header-icon i {
    font-size: 1.5rem;
    color: white;
  }

  .header-text h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    margin: 0;
  }

  .header-text p {
    color: rgba(255,255,255,0.7);
    margin: 0;
    font-size: 0.875rem;
  }

  /* Player Card - Table Style */
  .player-card {
    background: white;
    margin: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    overflow: hidden;
  }

  .detail-row {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
  }

  .detail-row:last-of-type {
    border-bottom: none;
  }

  .detail-label {
    width: 200px;
    padding: 1rem 1.5rem;
    background: #f9fafb;
    font-weight: 500;
    color: #374151;
    border-right: 1px solid #e5e7eb;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .detail-value {
    flex: 1;
    padding: 1rem 1.5rem;
    color: #1f2937;
    display: flex;
    align-items: center;
  }

  .required {
    color: #ef4444;
    margin-left: 4px;
  }

  /* Image Row */
  .image-row .detail-value {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
  }

  .image-cell {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .player-image-container {
    width: 120px;
    height: 120px;
    border: 3px solid var(--primary-color, #022a5e);
    border-radius: 8px;
    overflow: hidden;
    background: #f3f4f6;
  }

  .player-image-container img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .no-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 3rem;
  }

  .btn-upload {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: var(--primary-color, #022a5e);
    color: white;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .btn-upload:hover {
    background: var(--primary-hover, #053e88);
  }

  /* Form Inputs in Edit Mode */
  .detail-value input,
  .detail-value select,
  .detail-value textarea {
    width: 100%;
    padding: 0.65rem 0.85rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    transition: all 0.2s;
  }

  .detail-value input:focus,
  .detail-value select:focus,
  .detail-value textarea:focus {
    outline: none;
    border-color: var(--primary-color, #022a5e);
    box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
  }

  .detail-value textarea {
    min-height: 80px;
    resize: vertical;
  }

  /* Action Buttons */
  .action-buttons {
    display: flex;
    gap: 0.75rem;
    padding: 1.25rem 1.5rem;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    justify-content: center;
  }

  .btn-action {
    padding: 0.65rem 1.25rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .btn-edit {
    background: var(--primary-color, #022a5e);
    color: white;
  }

  .btn-edit:hover {
    background: var(--primary-hover, #053e88);
  }

  .btn-save {
    background: #059669;
    color: white;
  }

  .btn-save:hover:not(:disabled) {
    background: #047857;
  }

  .btn-delete {
    background: #dc2626;
    color: white;
  }

  .btn-delete:hover {
    background: #b91c1c;
  }

  .btn-cancel {
    background: #6b7280;
    color: white;
  }

  .btn-cancel:hover:not(:disabled) {
    background: #4b5563;
  }

  .btn-action:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .approved-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: #dcfce7;
    color: #166534;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
  }

  .approved-badge i {
    font-size: 1.1rem;
  }

  /* Loading & Error States */
  .loading-container,
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    gap: 1rem;
  }

  .loading-container i,
  .error-container i {
    font-size: 3rem;
    color: var(--primary-color, #022a5e);
  }

  .loading-container p,
  .error-container p {
    color: #6b7280;
    font-size: 1.1rem;
  }

  .btn-back-main {
    padding: 0.75rem 1.5rem;
    background: var(--primary-color, #022a5e);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
  }

  .btn-back-main:hover {
    background: var(--primary-hover, #053e88);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .player-card {
      margin: 1rem;
    }

    .detail-row {
      flex-direction: column;
    }

    .detail-label {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #e5e7eb;
      padding: 0.75rem 1rem;
    }

    .detail-value {
      padding: 0.75rem 1rem;
    }

    .action-buttons {
      flex-direction: column;
    }

    .btn-action {
      width: 100%;
      justify-content: center;
    }
  }
`

export default PlayerDetail
